import { useEffect } from "react";
import { toast } from "sonner";

/**
 * Prevents the installed PWA from closing abruptly when the device hardware
 * back button is pressed. Only active in standalone (installed) mode — in a
 * regular browser tab the platform back button works normally.
 *
 * Strategy: plant a sentinel history entry at mount. Count React Router
 * pushState calls to know how deep the user has navigated past the sentinel.
 * Real in-app back navigation just decrements the depth and is passed
 * through to React Router. Once depth reaches zero, the next back would
 * exit the app — that's when we show the "Press back again to exit" toast
 * and re-plant the sentinel so the app stays open. A second back press
 * within 2 s lets the PWA close.
 */
export default function usePWABackGuard() {
  // Window/document listeners + sentinel installation — runs once at mount.
  // `exitArmed`/`exitTimer` are local mutable state contained in the effect's
  // closure; they MUST NOT be hoisted into deps or React would re-install
  // the listeners on every render and break the back-press counter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const isStandalone =
      (typeof window !== "undefined" &&
        window.matchMedia?.("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;
    if (!isStandalone) return;

    // Plant the sentinel exactly once (StrictMode double-invokes effects in dev).
    if (!window.__pwaGuardInstalled) {
      window.__pwaGuardInstalled = true;
      window.__pwaGuardDepth = 0;
      window.history.pushState({ __pwaGuard: true }, "");

      // Patch pushState so we can count non-sentinel pushes (React Router nav).
      const origPush = window.history.pushState;
      window.history.pushState = function (state, ...rest) {
        if (!state || state.__pwaGuard !== true) {
          window.__pwaGuardDepth = (window.__pwaGuardDepth || 0) + 1;
        }
        return origPush.apply(this, [state, ...rest]);
      };
    }

    let exitArmed = false;
    let exitTimer = null;

    const onPop = () => {
      if ((window.__pwaGuardDepth || 0) > 0) {
        // Popping back through React Router navigation — let it through.
        window.__pwaGuardDepth -= 1;
        return;
      }
      // Depth is 0 → user is at the sentinel; next back would exit the app.
      if (exitArmed) {
        // Second back press within the window — allow exit.
        return;
      }
      // Re-plant the sentinel so this back press is consumed and the app stays open.
      window.history.pushState({ __pwaGuard: true }, "");
      exitArmed = true;
      toast("Press back again to exit", { duration: 2000 });
      if (exitTimer) clearTimeout(exitTimer);
      exitTimer = setTimeout(() => {
        exitArmed = false;
      }, 2000);
    };

    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (exitTimer) clearTimeout(exitTimer);
    };
  }, []);
}
